import { useMemo, useState } from 'react';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
} from '@/components/ui/styled-table';
import type { PendingGroupProposalWithSignatures } from '@/types/group-proposal';
import { GroupChangeDetailDialog } from './group-change-detail-dialog';
import { GroupProposalRow } from './group-proposal-row';
import { KeyedMutator } from 'swr';
import { Button } from '@/components/ui/button';

interface ReviewGroupChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposals: PendingGroupProposalWithSignatures[];
  onApplied: () => void;
  refreshProposals: KeyedMutator<PendingGroupProposalWithSignatures[]>;
}

export function ReviewGroupChangesDialog({
  open,
  onOpenChange,
  proposals,
  onApplied,
  refreshProposals,
}: ReviewGroupChangesDialogProps) {
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(
    null
  );
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  const selectedProposal = useMemo(() => {
    return proposals.find((p) => p.proposal.id === selectedProposalId) || null;
  }, [selectedProposalId, proposals]);

  const handleRowClick = (item: PendingGroupProposalWithSignatures) => {
    setSelectedProposalId(item.proposal.id);
    setIsDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setIsDetailDialogOpen(false);
    setSelectedProposalId(null);
  };

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={onOpenChange}
        title={`Review proposed changes to Groups (${proposals.length})`}
        noPadding
        bodyContainerClassName="bg-muted"
      >
        <div className="p-6">
          {proposals.length === 0 ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 text-center">
              <p className="text-sm text-muted-foreground">
                No proposed changes are waiting for review.
              </p>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          ) : (
            <StyledTableWrapper>
              <StyledTable>
                <StyledTableHeader>
                  <StyledTableRow>
                    <StyledTableHead className="w-[352px]">
                      Group name
                    </StyledTableHead>
                    <StyledTableHead>Proposed changes</StyledTableHead>
                    <StyledTableHead className="w-48"></StyledTableHead>
                    <StyledTableHead className="w-40"></StyledTableHead>
                  </StyledTableRow>
                </StyledTableHeader>
                <StyledTableBody>
                  {proposals.map((item) => (
                    <GroupProposalRow
                      key={item.proposal.id}
                      proposal={item}
                      onRowClick={handleRowClick}
                    />
                  ))}
                </StyledTableBody>
              </StyledTable>
            </StyledTableWrapper>
          )}
        </div>
      </FullscreenDialog>

      {selectedProposal && (
        <GroupChangeDetailDialog
          open={isDetailDialogOpen}
          onOpenChange={setIsDetailDialogOpen}
          proposedChange={selectedProposal}
          onChangeUpdated={() => {
            onApplied();
          }}
          onClose={handleDetailDialogClose}
          refreshProposals={refreshProposals}
          proposals={proposals}
        />
      )}
    </>
  );
}
